import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Paperclip, Pencil, Trash2 } from "lucide-react";
import AttachmentViewer from "@/components/AttachmentViewer";
import ExpenseForm from "./ExpenseForm";
import { EditEntryDialog } from "./EditDialogs";
import { money } from "./TreasureSummary";
import { toast } from "sonner";

export default function Expenses({ scope, categories, inflowCategories = [], onChanged }) {
  const [data, setData] = useState({ expenses: [], can_manage: false });
  const [view, setView] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/island/treasury/expenses", { params: { scope } }).then((r) => setData(r.data));
  useEffect(() => { load(); }, [scope]); // eslint-disable-line
  const del = async (e) => {
    if (!window.confirm(`Delete "${e.title}"?`)) return;
    try { await api.delete(`/island/treasury/expenses/${e.id}`); load(); onChanged(); } catch (err) { toast.error(errMsg(err)); }
  };
  const refresh = () => { load(); onChanged(); };
  return (
    <div className="space-y-4" data-testid="expenses">
      {data.can_manage && <ExpenseForm scope={scope} categories={categories} inflowCategories={inflowCategories} onAdded={refresh} />}
      <Card className="gold-ring glass p-4">
        <p className="label-eyebrow mb-3">Manual records · {scope}</p>
        <div className="divide-y divide-border/50">
          {data.expenses.map((e) => (
            <div key={e.id} className="flex items-center gap-3 py-2.5" data-testid={`expense-${e.id}`}>
              <Badge className={`text-[0.6rem] ${(e.kind || "out") === "in" ? "bg-emerald-500/15 text-emerald-700" : "bg-rose-500/15 text-rose-700"}`}>{(e.kind || "out") === "in" ? "In" : "Out"}</Badge>
              <div className="min-w-0 flex-1"><p className="truncate font-600">{e.title}</p><p className="text-[0.65rem] text-muted-foreground">{e.date} · {e.category} · by {e.created_by}{e.edited_by ? ` · edited by ${e.edited_by}` : ""}{e.note ? ` · ${e.note}` : ""}</p></div>
              {e.attachment && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setView(e)} data-testid={`expense-view-${e.id}`}><Paperclip className="mr-1 h-3.5 w-3.5" />Receipt</Button>}
              <p className={`font-mono-data font-700 ${(e.kind || "out") === "in" ? "text-emerald-600" : "text-rose-600"}`}>{(e.kind || "out") === "in" ? "+" : "−"}{money(e.currency, e.amount)}</p>
              {data.can_manage && <><Button size="sm" variant="ghost" className="h-7" onClick={() => setEdit(e)} data-testid={`expense-edit-${e.id}`}><Pencil className="h-4 w-4" /></Button><Button size="sm" variant="ghost" className="h-7 text-destructive" onClick={() => del(e)} data-testid={`expense-delete-${e.id}`}><Trash2 className="h-4 w-4" /></Button></>}
            </div>
          ))}
          {data.expenses.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No manual records for {scope}.</p>}
        </div>
      </Card>
      {view && <AttachmentViewer title={view.title} items={[{ url: `/island/treasury/expenses/${view.id}/attachment`, label: view.attachment?.name, content_type: view.attachment?.content_type }]} onClose={() => setView(null)} />}
      {edit && <EditEntryDialog entry={edit} categories={categories} inflowCategories={inflowCategories} onClose={() => setEdit(null)} onSaved={refresh} />}
    </div>
  );
}
